import { redirectToCodePage } from '@/api/mfaRequired';
import { issueContentTicket } from '@/api2/fileTickets';
import { probeVideoContent } from '@/api2/videoBridge';
import { keyonFileUrl } from '@/utils/fileUrl';

/**
 * Playing and saving a video.
 *
 * A <video> element that fails says only "error": it cannot tell a container
 * this browser cannot read from a 409, 410 or 401 whose JSON body it was handed
 * instead of a video. probeKeyonVideo asks the same question the element asked,
 * for one byte, so the answer can be mapped to a state. A HEAD would not do: it
 * has no body, so it cannot carry `mfa_required`. (A video codec it lacks,
 * iPhone HEVC on desktop Chrome or Firefox, often fires no error at all and
 * plays the audio alone: AttachmentVideoPlayer.vue catches that by the
 * picture's size, not here.)
 *
 * The fallback is a plain navigation to a download, never the axios blob path
 * in keyonFiles.js: that buffers the whole file in a JS Blob, which a phone
 * cannot hold for a 100 MiB video, while a navigation lets the browser write
 * to disk as the bytes arrive.
 *
 * The port (#334): main asked keyon's file.php with the session cookie. Here
 * there is no keyon and no cookie, so the probe is the API's own content route
 * with the bearer token (src/api2/videoBridge.js probeVideoContent), and the
 * download is a single-use download ticket (decision D5, src/api2/fileTickets.js)
 * - an API URL, never a storage URL.
 *
 * Both functions take injectable `{ http, probe, issueTicket, doc,
 * onMfaRequired, timeoutMs }`, so they can be tested without a browser or a
 * router.
 */

// A probe that gets no answer must end: the card would otherwise wait for good.
const PROBE_TIMEOUT_MS = 15000;

function defaults(deps = {}) {
  return {
    doc: deps.doc || (typeof document !== 'undefined' ? document : null),
    onMfaRequired: deps.onMfaRequired || (() => redirectToCodePage()),
    timeoutMs: typeof deps.timeoutMs === 'number' ? deps.timeoutMs : PROBE_TIMEOUT_MS,
  };
}

/**
 * The same question the media element asks, for one byte, of the API.
 *
 * ok means the API would serve the video, so a failure of the element is the
 * browser's (codec or container). Anything else is a state a page can show.
 * `mfa_required` has already sent the person to sign in again, as a download's
 * does.
 * @returns {Promise<{ok:true}|{ok:false,state:'scan_pending'|'unavailable'|'mfa_required'|'rate_limited'|'error'}>}
 */
export async function probeKeyonVideo(uid, deps) {
  // The port (#334): the same one-byte question, asked of the API with the
  // bearer token (src/api2/videoBridge.js probeVideoContent), because there is
  // no keyon and no cookie for a fetch of file.php to carry.
  if (!keyonFileUrl(uid)) return { ok: false, state: 'unavailable' };
  const { onMfaRequired, timeoutMs } = defaults(deps);
  const probe = (deps && deps.probe) || probeVideoContent;
  let result;
  try {
    result = await probe(uid, { ...(deps || {}), timeoutMs });
  } catch (_) {
    return { ok: false, state: 'error' };
  }
  if (result && result.ok) return { ok: true };
  const state = (result && result.state) || 'error';
  if (state === 'mfa_required') onMfaRequired();
  return { ok: false, state };
}

/**
 * Save a video. Probes first, so a 409 or 410 becomes a state on the card
 * instead of a JSON error page the browser would try to save; on ok it clicks
 * one anchor to a download ticket and leaves the transfer to the browser.
 * The `download` attribute keeps even a late refusal from replacing the page.
 * Same result shape as downloadKeyonFile.
 * @returns {Promise<{ok:true}|{ok:false,state:'scan_pending'|'unavailable'|'mfa_required'|'rate_limited'|'error'}>}
 */
export async function startKeyonFileDownload(uid, filename, deps) {
  if (!keyonFileUrl(uid, { download: true })) return { ok: false, state: 'unavailable' };
  const probe = await probeKeyonVideo(uid, deps);
  if (!probe.ok) return probe;
  const { doc, onMfaRequired } = defaults(deps);
  if (!doc) return { ok: false, state: 'error' };
  // The port (#334): a single-use download ticket (decision D5), an API URL
  // the browser streams to disk - never file.php, which nothing answers here,
  // and never the bridged axios blob path, which would hold the whole file.
  const ticket = await ((deps && deps.issueTicket) || issueContentTicket)(uid, { download: true });
  if (!ticket || !ticket.ok) {
    const state = (ticket && ticket.state) || 'error';
    if (state === 'mfa_required') onMfaRequired();
    return { ok: false, state };
  }
  const a = doc.createElement('a');
  a.href = ticket.url;
  a.download = filename || 'download';
  a.rel = 'noopener';
  if (doc.body && typeof doc.body.appendChild === 'function') doc.body.appendChild(a);
  a.click();
  if (typeof a.remove === 'function') a.remove();
  return { ok: true };
}
